import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { ChevronRight, RefreshCw } from 'lucide-react';
import { ORDER_TRANSITIONS, TAKEAWAY_TRANSITIONS, canTransition } from '@/lib/domain';
import {
  FulfillmentType,
  OrderStatus,
  TakeawayStatus,
  VENUE_EVENTS,
  type OrderEventPayload,
  type OrderItemEventPayload,
} from '@/lib/shared-types';
import { api } from '@/lib/api';
import { useRealtime } from '@/Hooks/useRealtime';
import { useAppDispatch } from '@/Redux/store';
import {
  applyItemEvent,
  applyOrderEvent,
  invalidateBoards,
  invalidateOrder,
  invalidateOrderLists,
  useListOrdersQuery,
  useListTakeawayOrdersQuery,
  type OrderListQuery,
  type OrderRow,
  type TakeawayListQuery,
} from '@/Redux/Slices/operationsApi';
import { useCurrency } from '@/Hooks/useCurrency';
import { usePermission } from '@/Hooks/usePermission';
import { Tabs } from '@/components/ui/tabs';
import { useRoutedTabs } from '@/Pages/routedTabs';
import { ORDER_TABS } from '@/Pages/tabRoutes';
import { Badge, toneForStatus } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog } from '@/components/ui/dialog';
import { Field, Select, Textarea } from '@/components/ui/field';
import { Icon } from '@/components/ui/icon';
import { Input } from '@/components/ui/input';
import { LoadingState, ErrorState } from '@/components/ui/states';
import { TableWrap, Table, THead, TH, TR, TD, TableEmpty } from '@/components/ui/table';
import { AwaitingConfirmationLane } from './AwaitingConfirmationLane';
import { OrderDetailPanel } from './components/OrderDetailPanel';
import type { OrderDetailRow } from './orderDetail';
import { apiMessage, formatDateTime, formatTime, useBranches } from './shared';

const ORDER_STATUSES = Object.values(OrderStatus);
const TAKEAWAY_STATUSES = Object.values(TakeawayStatus);
const FULFILLMENT_TYPES = Object.values(FulfillmentType);

/**
 * `OrderRow` / `OrderItemRow` are declared in `Redux/Slices/operationsApi` now:
 * the shape the cache holds and the shape this table reads have to be one type,
 * or a socket patch can quietly write a field the renderer never shows.
 */
const COLUMNS = 8;

/**
 * P5-04 / P8-06 — the live order board, and one order in full (64:8).
 *
 * Two lanes over one order model (ADR-003): the dine-in list, and the takeaway
 * queue whose extra step is the §8.2 release. Rows repaint from the `ORDER_*`
 * and `ITEM_*` events (§27) instead of polling, so a station touching a line
 * moves this board in the same second.
 *
 * Picking an order swaps the board for `OrderDetailPanel`. The frame draws that
 * as a route (`Orders > O-260821-0025`) and the app has flat list routes only —
 * `Routes.tsx` is nobody's to change here — so the detail is rendered in place
 * and the trail is the way back. It reads the row straight out of the cache
 * rather than refetching, which is what keeps the live patching working while
 * it is open.
 *
 * The row expansion it replaces is gone: an order was inspected in a table cell
 * that could not hold the totals, the timeline or the payment state, and having
 * both would leave two places showing the same order differently.
 */

export default function Orders() {
  const { t, i18n } = useTranslation();
  const { can } = usePermission();
  const branches = useBranches();
  const dispatch = useAppDispatch();

  const routed = useRoutedTabs('/orders', ORDER_TABS);
  const tab = routed.open ? routed.current : null;
  const [status, setStatus] = useState('');
  const [takeawayStatus, setTakeawayStatus] = useState('');
  const [fulfillmentType, setFulfillmentType] = useState('');
  const [branchId, setBranchId] = useState('');
  const [day, setDay] = useState('');
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [action, setAction] = useState<{ kind: 'cancel' | 'void'; order: OrderRow } | null>(null);
  const [reason, setReason] = useState('');

  // D-04 — an order total is money: it needs the branch's currency code and its
  // minor-unit digits, not a bare two-decimal number. The board's own branch
  // filter picks the branch; with "All branches" it follows the header switcher.
  const { format: money } = useCurrency(branchId || undefined);

  const isTakeaway = tab === 'takeaway';

  /**
   * The selected order belongs to the tab it was opened from, so leaving that
   * tab has to let go of it — including when the tab was left with the back
   * button rather than by clicking the other one.
   */
  useEffect(() => setSelectedId(null), [tab]);

  /**
   * A day filter is two inclusive instants, not a date string — `OrderQueryDto`
   * windows on `createdAt` (I-09), and the browser's own timezone is the one the
   * operator means.
   *
   * `TakeawayQueryDto` declares no `from`/`to`, and `forbidNonWhitelisted` turns
   * an undeclared param into a 400 rather than an ignored one — so the bounds go
   * to `/orders` only. Spreading them into both tabs, as this screen used to,
   * made every dated search on the takeaway tab fail outright.
   */
  const dineInArgs = useMemo<OrderListQuery>(
    () => ({
      limit: 100,
      status,
      fulfillmentType,
      branchId,
      search,
      ...(day
        ? {
            from: new Date(`${day}T00:00:00`).toISOString(),
            to: new Date(`${day}T23:59:59.999`).toISOString(),
          }
        : {}),
    }),
    [status, fulfillmentType, branchId, search, day],
  );

  const takeawayArgs = useMemo<TakeawayListQuery>(
    () => ({ limit: 100, status: takeawayStatus, branchId, search }),
    [takeawayStatus, branchId, search],
  );

  /**
   * The awaiting lane reads whichever branch this board's own filter names, so
   * it has to listen there too — a lane showing branch B while subscribed to
   * the header's branch A is live, silent, and looks like a quiet venue. With
   * no filter set, `useRealtime` falls back to the active branch, which is the
   * branch this board is then showing.
   */
  const awaitingRooms = useMemo(
    () => (branchId ? [`branch:${branchId}`] : undefined),
    [branchId],
  );

  // Cached: coming back to this screen paints the last answer immediately and
  // revalidates behind it, instead of the blank-then-fill `useApi` gave.
  const dineIn = useListOrdersQuery(dineInArgs, { skip: isTakeaway });
  const takeaway = useListTakeawayOrdersQuery(takeawayArgs, { skip: !isTakeaway });
  const active = isTakeaway ? takeaway : dineIn;

  const rows = active.data?.rows ?? [];
  // `isLoading` is the first load only; `isFetching` covers a revalidation over
  // rows that are already on screen, which must not blank the table.
  const loading = active.isLoading;
  const fetching = active.isFetching;
  const error = active.error
    ? ((active.error as { message?: string }).message ?? t('operations.errors.actionFailed'))
    : null;
  const reload = active.refetch;

  /**
   * The order the detail panel is showing, read out of the same cache entry the
   * board renders — so an `ITEM_READY` arriving while it is open repaints it.
   * A filter change that drops the row falls back to the board rather than
   * holding a stale copy of an order this filter no longer matches.
   */
  const selected = selectedId
    ? ((rows.find((row) => row.id === selectedId) as OrderDetailRow | undefined) ?? null)
    : null;

  // ------------------------------------------------------------- realtime

  /*
   * The events patch the cache, not a second copy of the rows in local state.
   * That was the thing that could disagree with itself: `setRows` held the live
   * truth while the fetch layer held the fetched one, so any refetch — a filter
   * change, a remount — silently reverted every event that had arrived since.
   * Patching the cache entry means the render and the cache are the same object,
   * and the patch survives the trip to another screen and back.
   *
   * It is also the cheap path: `updateQueryData` writes the payload straight in,
   * with no request behind it.
   */
  const onOrderEvent = useCallback(
    (payload: OrderEventPayload) => {
      dispatch(applyOrderEvent(payload));
    },
    [dispatch],
  );

  const onItemEvent = useCallback(
    (payload: OrderItemEventPayload) => {
      dispatch(applyItemEvent(payload));
    },
    [dispatch],
  );

  const onTakeawayEvent = useCallback(
    (payload: OrderEventPayload) => {
      dispatch(applyOrderEvent(payload, 'takeawayStatus'));
    },
    [dispatch],
  );

  // A brand-new order is in no cache entry yet and the payload carries no row,
  // so this is the one event that has to spend a request.
  useRealtime<OrderEventPayload>(VENUE_EVENTS.ORDER_CREATED, () => {
    dispatch(invalidateOrderLists());
  });
  useRealtime<OrderEventPayload>(VENUE_EVENTS.ORDER_CONFIRMED, onOrderEvent);
  useRealtime<OrderEventPayload>(VENUE_EVENTS.ORDER_SENT_TO_KITCHEN, onOrderEvent);
  useRealtime<OrderEventPayload>(VENUE_EVENTS.ORDER_READY, onOrderEvent);
  /* `ORDER_COMPLETED` has no emitter anywhere in the backend today. The
     subscription stays because the handler is the one four live events share
     and dropping it would be a second code path to keep in step; nothing on
     this screen is built to depend on it arriving. */
  useRealtime<OrderEventPayload>(VENUE_EVENTS.ORDER_COMPLETED, onOrderEvent);
  useRealtime<OrderItemEventPayload>(VENUE_EVENTS.ITEM_PREPARING, onItemEvent);
  useRealtime<OrderItemEventPayload>(VENUE_EVENTS.ITEM_READY, onItemEvent);
  useRealtime<OrderItemEventPayload>(VENUE_EVENTS.ITEM_PICKED_UP, onItemEvent);
  useRealtime<OrderItemEventPayload>(VENUE_EVENTS.ITEM_SERVED, onItemEvent);
  useRealtime<OrderEventPayload>(VENUE_EVENTS.TAKEAWAY_RELEASED, onTakeawayEvent);
  useRealtime<OrderEventPayload>(VENUE_EVENTS.TAKEAWAY_READY, onTakeawayEvent);
  useRealtime<OrderEventPayload>(VENUE_EVENTS.TAKEAWAY_HANDED_OVER, onTakeawayEvent);

  // --------------------------------------------------------------- actions

  /**
   * Writes stay on the axios client — `baseApi` is deliberately not a write path
   * for orders (its header says why: an in-memory mutation is lost with the tab,
   * durable staff writes belong to `@/lib/offline`). What changes is the
   * aftermath: instead of reloading this one screen, the call says which tags it
   * dirtied, so the kitchen board holding the same order refetches too.
   */
  const run = useCallback(
    async (
      key: string,
      url: string,
      body: Record<string, unknown>,
      successKey: string,
      orderId: string,
      touchesTickets = true,
    ) => {
      setBusy(key);
      try {
        await api.post(url, body);
        toast.success(t(successKey));
        dispatch(invalidateOrder(orderId));
        dispatch(invalidateOrderLists());
        if (touchesTickets) dispatch(invalidateBoards());
      } catch (e) {
        toast.error(apiMessage(e, t('operations.errors.actionFailed')));
      } finally {
        setBusy(null);
      }
    },
    [dispatch, t],
  );

  const closeDialog = () => {
    setAction(null);
    setReason('');
  };

  const submitReason = async () => {
    if (!action) return;
    const { kind, order } = action;
    await run(
      `${kind}:${order.id}`,
      `/orders/${order.id}/${kind}`,
      { reason: reason.trim() || undefined },
      kind === 'cancel' ? 'operations.orders.cancelled' : 'operations.orders.voided',
      order.id,
    );
    closeDialog();
  };

  /**
   * Every action the controller exposes for one order, gated exactly as the
   * server gates it: the permission key **and** the state machine.
   *
   * One function so the board row and the detail header cannot drift apart —
   * the frame draws Void beside a SERVED order, and a frame is not a transition
   * table. `canTransition` decides, here as before.
   */
  const actionsFor = (order: OrderRow) => (
    <>
      {isTakeaway &&
        can('orders.send') &&
        canTransition(
          TAKEAWAY_TRANSITIONS,
          order.takeawayStatus ?? TakeawayStatus.DRAFT,
          TakeawayStatus.RELEASED,
        ) && (
          <Button
            size="sm"
            disabled={busy === `release:${order.id}`}
            onClick={() =>
              run(
                `release:${order.id}`,
                `/takeaway/${order.id}/release`,
                {},
                'operations.orders.released',
                order.id,
              )
            }
          >
            {t('operations.orders.release')}
          </Button>
        )}

      {isTakeaway &&
        can('kitchen.update') &&
        canTransition(
          TAKEAWAY_TRANSITIONS,
          order.takeawayStatus ?? TakeawayStatus.DRAFT,
          TakeawayStatus.READY,
        ) && (
          <Button
            size="sm"
            variant="outline"
            disabled={busy === `ready:${order.id}`}
            onClick={() =>
              run(
                `ready:${order.id}`,
                `/takeaway/${order.id}/ready`,
                {},
                'platform.takeaway.marked',
                order.id,
              )
            }
          >
            {t('platform.takeaway.ready')}
          </Button>
        )}

      {/* READY is not HANDED_OVER: the bag is only gone once somebody has
          walked out with it, and COMPLETED closes the sale after that. */}
      {isTakeaway &&
        can('orders.update') &&
        canTransition(
          TAKEAWAY_TRANSITIONS,
          order.takeawayStatus ?? TakeawayStatus.DRAFT,
          TakeawayStatus.HANDED_OVER,
        ) && (
          <Button
            size="sm"
            disabled={busy === `handover:${order.id}`}
            onClick={() =>
              run(
                `handover:${order.id}`,
                `/takeaway/${order.id}/handover`,
                {},
                'platform.takeaway.handedOver',
                order.id,
              )
            }
          >
            {t('platform.takeaway.handover')}
          </Button>
        )}

      {isTakeaway &&
        can('orders.update') &&
        canTransition(
          TAKEAWAY_TRANSITIONS,
          order.takeawayStatus ?? TakeawayStatus.DRAFT,
          TakeawayStatus.COMPLETED,
        ) && (
          <Button
            size="sm"
            variant="outline"
            disabled={busy === `complete:${order.id}`}
            onClick={() =>
              run(
                `complete:${order.id}`,
                `/takeaway/${order.id}/complete`,
                {},
                'platform.takeaway.completed',
                order.id,
              )
            }
          >
            {t('platform.takeaway.complete')}
          </Button>
        )}

      {can('orders.update') &&
        canTransition(ORDER_TRANSITIONS, order.status, OrderStatus.CONFIRMED) && (
          <Button
            size="sm"
            variant="outline"
            disabled={busy === `confirm:${order.id}`}
            onClick={() =>
              run(
                `confirm:${order.id}`,
                `/orders/${order.id}/confirm`,
                {},
                'operations.orders.confirmed',
                order.id,
                false,
              )
            }
          >
            {t('common.confirm')}
          </Button>
        )}

      {can('orders.send') &&
        canTransition(ORDER_TRANSITIONS, order.status, OrderStatus.SENT_TO_KITCHEN) && (
          <Button
            size="sm"
            disabled={busy === `send:${order.id}`}
            onClick={() =>
              run(
                `send:${order.id}`,
                `/orders/${order.id}/send`,
                {},
                'operations.orders.sent',
                order.id,
              )
            }
          >
            {t('order.sendToKitchen')}
          </Button>
        )}

      {can('orders.cancel') &&
        canTransition(ORDER_TRANSITIONS, order.status, OrderStatus.CANCELLED) && (
          <Button size="sm" variant="outline" onClick={() => setAction({ kind: 'cancel', order })}>
            {t('common.cancel')}
          </Button>
        )}

      {can('orders.void') &&
        canTransition(ORDER_TRANSITIONS, order.status, OrderStatus.VOIDED) && (
          <Button size="sm" variant="danger" onClick={() => setAction({ kind: 'void', order })}>
            {t('operations.orders.void')}
          </Button>
        )}
    </>
  );

  // The server filters by date now (I-09), so what came back IS the answer —
  // no client-side narrowing, which could only ever filter the page already loaded.
  const visible = rows;

  if (!routed.open) return routed.redirect;
  const { tabs, current, select } = routed;

  const clearFilters = () => {
    setStatus('');
    setTakeawayStatus('');
    setFulfillmentType('');
    setBranchId('');
    setDay('');
    setSearch('');
  };

  return (
    <div className="space-y-4">
      {selected ? (
        <OrderDetailPanel
          order={selected}
          lifecycleStatus={
            isTakeaway ? (selected.takeawayStatus ?? selected.status) : selected.status
          }
          isTakeaway={isTakeaway}
          actions={actionsFor(selected)}
          onBack={() => setSelectedId(null)}
        />
      ) : (
        <>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h1 className="text-2xl font-semibold">{t('nav.orders')}</h1>
            <div className="flex items-center gap-3">
              {fetching && !loading && (
                <span className="text-xs text-ink-muted">{t('operations.common.updating')}</span>
              )}
              <Button variant="outline" onClick={() => reload()} disabled={fetching}>
                <Icon as={RefreshCw} />
                {t('operations.common.refresh')}
              </Button>
            </div>
          </div>

          <Tabs
            tabs={tabs.map((entry) => ({ key: entry.id, label: t(entry.labelKey) }))}
            value={current}
            onChange={select}
            label={t('nav.orders')}
            idPrefix="orders"
          />

          {/*
            A guest order that has stopped for a waiter is a dine-in order — the
            guest scanned a table — so it is drawn on the dine-in tab only.
            Repeating it on the takeaway queue would put one list on screen
            twice with no filter distinguishing the copies.

            It sits above the filters rather than inside the table because it is
            not a subset of what the table shows: the table honours whatever
            status, date and search the operator last left set, and an order
            waiting to be read back must not be hideable by a filter nobody
            remembers applying.
          */}
          {!isTakeaway && (
            <AwaitingConfirmationLane branchId={branchId} rooms={awaitingRooms} />
          )}

          <Card>
            <CardContent className="grid gap-3 p-4 [&>*]:min-w-0 sm:grid-cols-2 lg:grid-cols-5">
              <Field label={t('operations.filters.search')}>
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder={t('operations.orders.searchHint')}
                />
              </Field>

              {isTakeaway ? (
                <Field label={t('operations.filters.status')}>
                  <Select
                    value={takeawayStatus}
                    onChange={(e) => setTakeawayStatus(e.target.value)}
                  >
                    <option value="">{t('operations.filters.any')}</option>
                    {TAKEAWAY_STATUSES.map((s) => (
                      <option key={s} value={s}>
                        {t(`operations.takeawayStatus.${s}`)}
                      </option>
                    ))}
                  </Select>
                </Field>
              ) : (
                <>
                  <Field label={t('operations.filters.status')}>
                    <Select value={status} onChange={(e) => setStatus(e.target.value)}>
                      <option value="">{t('operations.filters.any')}</option>
                      {ORDER_STATUSES.map((s) => (
                        <option key={s} value={s}>
                          {t(`operations.orderStatus.${s}`)}
                        </option>
                      ))}
                    </Select>
                  </Field>

                  <Field label={t('operations.filters.fulfillment')}>
                    <Select
                      value={fulfillmentType}
                      onChange={(e) => setFulfillmentType(e.target.value)}
                    >
                      <option value="">{t('operations.filters.any')}</option>
                      {FULFILLMENT_TYPES.map((f) => (
                        <option key={f} value={f}>
                          {t(`order.fulfillment.${f}`)}
                        </option>
                      ))}
                    </Select>
                  </Field>
                </>
              )}

              {branches.length > 0 && (
                <Field label={t('operations.filters.branch')}>
                  <Select value={branchId} onChange={(e) => setBranchId(e.target.value)}>
                    <option value="">{t('operations.filters.allBranches')}</option>
                    {branches.map((b) => (
                      <option key={b.id} value={b.id}>
                        {b.name}
                      </option>
                    ))}
                  </Select>
                </Field>
              )}

              <Field label={t('operations.filters.date')}>
                <Input type="date" value={day} onChange={(e) => setDay(e.target.value)} />
              </Field>

              <div className="flex items-end">
                <Button variant="ghost" onClick={clearFilters}>
                  {t('operations.filters.clear')}
                </Button>
              </div>
            </CardContent>
          </Card>

          {error ? (
            <ErrorState message={error} onRetry={reload} />
          ) : loading && !rows.length ? (
            <LoadingState label={t('common.loading')} />
          ) : (
            <TableWrap>
              <Table>
                <THead>
                  <TR>
                    <TH>{t('operations.orders.number')}</TH>
                    <TH>{t('operations.orders.customer')}</TH>
                    <TH>{t('operations.filters.fulfillment')}</TH>
                    <TH>{t('operations.filters.status')}</TH>
                    <TH>{t('operations.orders.items')}</TH>
                    <TH className="text-end">{t('common.total')}</TH>
                    <TH className="text-end">{t('operations.common.actions')}</TH>
                    <TH className="w-10" />
                  </TR>
                </THead>
                <tbody>
                  {!visible.length ? (
                    <TableEmpty colSpan={COLUMNS}>{t('operations.orders.empty')}</TableEmpty>
                  ) : (
                    visible.map((order) => {
                      const lifecycle = isTakeaway
                        ? (order.takeawayStatus ?? order.status)
                        : order.status;

                      return (
                        <TR key={order.id}>
                          <TD>
                            <button
                              type="button"
                              onClick={() => setSelectedId(order.id)}
                              className="font-medium text-accent underline"
                            >
                              {order.number}
                            </button>
                            <div className="text-xs text-ink-muted">
                              {formatDateTime(order.createdAt, i18n.language)}
                            </div>
                          </TD>

                          <TD>
                            <div>{order.customerName ?? '—'}</div>
                            {order.customerPhone && (
                              <div className="text-xs text-ink-muted" dir="ltr">
                                {order.customerPhone}
                              </div>
                            )}
                          </TD>

                          <TD>
                            <span className="text-sm">
                              {t(`order.fulfillment.${order.fulfillmentType}`)}
                            </span>
                            {order.scheduledFor && (
                              <div className="text-xs text-ink-muted">
                                {t('operations.orders.scheduledFor', {
                                  time: formatTime(order.scheduledFor, i18n.language),
                                })}
                              </div>
                            )}
                          </TD>

                          <TD>
                            <Badge tone={toneForStatus(lifecycle)}>
                              {isTakeaway
                                ? t(`operations.takeawayStatus.${lifecycle}`)
                                : t(`operations.orderStatus.${lifecycle}`)}
                            </Badge>
                          </TD>

                          <TD>{order.items?.length ?? 0}</TD>

                          <TD className="text-end font-medium">{money(order.total)}</TD>

                          <TD>
                            <div className="flex flex-wrap justify-end gap-1.5">
                              {actionsFor(order)}
                            </div>
                          </TD>

                          <TD>
                            <button
                              type="button"
                              onClick={() => setSelectedId(order.id)}
                              aria-label={t('operations.orders.openDetail', {
                                number: order.number,
                              })}
                              className="rounded p-1 hover:bg-hover"
                            >
                              <Icon as={ChevronRight} />
                            </button>
                          </TD>
                        </TR>
                      );
                    })
                  )}
                </tbody>
              </Table>
            </TableWrap>
          )}
        </>
      )}

      <Dialog
        open={!!action}
        onClose={closeDialog}
        title={
          action?.kind === 'void'
            ? t('operations.orders.voidTitle')
            : t('operations.orders.cancelTitle')
        }
        description={t('operations.orders.reasonHint')}
        footer={
          <>
            <Button variant="ghost" onClick={closeDialog}>
              {t('common.cancel')}
            </Button>
            <Button variant="danger" onClick={submitReason} disabled={!!busy}>
              {t('common.confirm')}
            </Button>
          </>
        }
      >
        <Field label={t('operations.common.reason')}>
          <Textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)} />
        </Field>
      </Dialog>
    </div>
  );
}
